import {
  PII_OBFUSCATION_STORAGE_KEY,
  PII_DATA_TYPES,
  PII_COLUMN_NAME_PATTERNS,
} from "../../../constants";

/**
 * Get the obfuscation state from localStorage
 */
export const getPiiObfuscationState = (): boolean => {
  const stored = localStorage.getItem(PII_OBFUSCATION_STORAGE_KEY);
  return stored === "true";
};

/**
 * Set the obfuscation state in localStorage
 */
export const setPiiObfuscationState = (enabled: boolean): void => {
  localStorage.setItem(PII_OBFUSCATION_STORAGE_KEY, String(enabled));
};

/**
 * Check if a column name matches any PII name patterns
 */
const matchesNamePattern = (
  columnName: string,
  patterns: readonly string[]
): boolean => {
  const lowerName = columnName.toLowerCase().replace(/[_\s-]/g, "");
  return patterns.some((pattern) => {
    const normalizedPattern = pattern.toLowerCase().replace(/[_\s-]/g, "");
    return lowerName === normalizedPattern || lowerName.includes(normalizedPattern);
  });
};

/**
 * Determine what type of PII a column contains based on data type and column name
 */
export type PiiType = "email" | "phone" | "linkedin_url" | "fullName" | "lastName" | "personal" | null;

export const detectPiiType = (dataType: string, columnName: string): PiiType => {
  // First check by data type
  if (PII_DATA_TYPES.includes(dataType as any)) {
    return dataType as PiiType;
  }

  // Then check by column name patterns
  // Note: First name is NOT obfuscated
  if (matchesNamePattern(columnName, PII_COLUMN_NAME_PATTERNS.lastName)) {
    return "lastName";
  }
  if (matchesNamePattern(columnName, PII_COLUMN_NAME_PATTERNS.fullName)) {
    return "fullName";
  }
  if (matchesNamePattern(columnName, PII_COLUMN_NAME_PATTERNS.personal)) {
    return "personal";
  }

  return null;
};

/**
 * Obfuscate an email address
 * Example: john.doe@company.com -> j***@c***.com
 */
export const obfuscateEmail = (email: string): string => {
  if (!email || !email.includes("@")) return email;

  const [localPart, domain] = email.split("@");
  const [domainName, ...tld] = domain.split(".");

  const obfuscatedLocal = localPart.charAt(0) + "***";
  const obfuscatedDomain = domainName.charAt(0) + "***";
  const tldPart = tld.join(".");

  return `${obfuscatedLocal}@${obfuscatedDomain}.${tldPart}`;
};

/**
 * Obfuscate a phone number
 * Example: +1-555-123-4567 -> ***-***-4567
 */
export const obfuscatePhone = (phone: string): string => {
  if (!phone) return phone;

  // Keep only the last 4 digits visible
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length < 4) return "***";

  const lastFour = digitsOnly.slice(-4);
  return `***-***-${lastFour}`;
};

/**
 * Obfuscate a LinkedIn URL
 * Example: https://linkedin.com/in/johndoe -> https://linkedin.com/in/j***
 */
export const obfuscateLinkedInUrl = (url: string): string => {
  if (!url) return url;

  // Match common LinkedIn URL patterns
  const patterns = [
    /^(https?:\/\/(?:www\.)?linkedin\.com\/in\/)([^/?\s]+)/i,
    /^(https?:\/\/(?:www\.)?linkedin\.com\/company\/)([^/?\s]+)/i,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      const prefix = match[1];
      const identifier = match[2];
      const obfuscatedId = identifier.charAt(0) + "***";
      return `${prefix}${obfuscatedId}`;
    }
  }

  return url;
};

/**
 * Obfuscate a name showing only first 3 characters (like Apollo)
 * Example: Johnson -> Joh***
 */
export const obfuscateName = (name: string, visibleChars: number = 3): string => {
  if (!name) return name;

  const trimmedName = name.trim();
  if (trimmedName.length <= visibleChars) {
    return trimmedName + "***";
  }

  return trimmedName.substring(0, visibleChars) + "***";
};

/**
 * Obfuscate a full name (first + last)
 * Example: John Doe -> J*** D***
 */
export const obfuscateFullName = (fullName: string): string => {
  if (!fullName) return fullName;

  const parts = fullName.trim().split(/\s+/);
  return parts.map((part) => part.charAt(0) + "***").join(" ");
};

/**
 * Check if a PII type should use blur effect instead of text replacement
 * Blur is used for: email, phone, linkedin_url, fullName, personal
 * Text replacement (first 3 chars) is used for: lastName
 */
export const shouldUseBlur = (piiType: PiiType): boolean => {
  return piiType === "email" || piiType === "phone" || piiType === "linkedin_url" || piiType === "fullName" || piiType === "personal";
};

/**
 * Obfuscate a value based on detected PII type
 * Note: For blur fields, this returns the original value (blur is applied via CSS)
 */
export const obfuscateValue = (value: string, piiType: PiiType): string => {
  if (!value || !piiType) return value;

  switch (piiType) {
    case "email":
    case "phone":
    case "linkedin_url":
    case "fullName":
    case "personal":
      // These use CSS blur, return original value
      return value;
    case "lastName":
      return obfuscateName(value, 3);
    default:
      return value;
  }
};

/**
 * Check if a column should be obfuscated based on its data type and name
 */
export const shouldObfuscateColumn = (dataType: string, columnName: string): boolean => {
  return detectPiiType(dataType, columnName) !== null;
};
